import { useEffect, useState } from "react"
import { useToast, ToastMessage } from "@stayframe/ui"
import { formatApiError } from "@/lib/api"
import { opsAdminApi } from "@/lib/opsAdminApi"

interface GateConfig {
  ritual_enabled: boolean
  cooldown_minutes: number
  guidebook_grace_ms: number
}

const DEFAULTS: GateConfig = {
  ritual_enabled: true,
  cooldown_minutes: 30,
  guidebook_grace_ms: 300,
}

export function OpsIntelligenceGateCard() {
  const { toast } = useToast()
  const [config, setConfig] = useState<GateConfig>(DEFAULTS)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      const res = await opsAdminApi.getIntelligenceGateConfig()
      if (cancelled) return
      if (res.ok && res.config) {
        setConfig(res.config)
      }
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [])

  function update(patch: Partial<GateConfig>) {
    setConfig(prev => ({ ...prev, ...patch }))
    setDirty(true)
  }

  async function handleSave() {
    setSaving(true)
    const res = await opsAdminApi.saveIntelligenceGateConfig(config)
    if (res.ok && res.config) {
      setConfig(res.config)
      setDirty(false)
      toast("Intelligence gate config saved.", "success")
    } else {
      const error = formatApiError(res as unknown as Record<string, unknown>)
      toast(<ToastMessage message={error.message || "Failed to save."} requestId={error.requestId} />, "error")
    }
    setSaving(false)
  }

  if (loading) {
    return (
      <div className="section-card">
        <div className="section-card__title">Intelligence Gate</div>
        <div className="ov-skeleton ov-skeleton--text" style={{ width: "60%", height: 16 }} />
      </div>
    )
  }

  return (
    <div className="section-card">
      <div className="section-card__header-row" style={{ marginBottom: 16 }}>
        <div className="section-card__title" style={{ marginBottom: 0 }}>Intelligence Gate</div>
        <span className="ov-ai-badge" style={{ marginLeft: 8 }}>
          <span className="ov-ai-badge__dot" />
          AI
        </span>
      </div>
      <p style={{ fontSize: "var(--sf-text-sm)", color: "var(--sf-text-secondary)", marginBottom: 16 }}>
        Controls the AI loading state across host dashboards and the guest guidebook. Loading surfaces track genuine readiness only — there is no artificial delay.
      </p>

      <div className="ops-intel-gate-fields">
        <label className="ops-intel-gate-field">
          <span className="ops-intel-gate-field__label">Enable intelligence loading state</span>
          <input
            type="checkbox"
            checked={config.ritual_enabled}
            onChange={e => update({ ritual_enabled: e.target.checked })}
          />
        </label>

        <label className="ops-intel-gate-field">
          <span className="ops-intel-gate-field__label">Cooldown (minutes)</span>
          <input
            type="number"
            min={5}
            max={120}
            step={5}
            value={config.cooldown_minutes}
            onChange={e => update({ cooldown_minutes: Number(e.target.value) || 30 })}
            className="ops-intel-gate-field__input"
          />
          <span className="ops-intel-gate-field__hint">5–120</span>
        </label>

        <label className="ops-intel-gate-field">
          <span className="ops-intel-gate-field__label">Guidebook grace (ms)</span>
          <input
            type="number"
            min={100}
            max={1000}
            step={50}
            value={config.guidebook_grace_ms}
            onChange={e => update({ guidebook_grace_ms: Number(e.target.value) || 300 })}
            className="ops-intel-gate-field__input"
          />
          <span className="ops-intel-gate-field__hint">100–1000</span>
        </label>
      </div>

      <div style={{ marginTop: 16 }}>
        <button
          type="button"
          className="data-table__action"
          disabled={!dirty || saving}
          onClick={handleSave}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  )
}
